import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Menu, X, User, Handshake } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { useCustomerAuth } from "../../context/CustomerAuthContext";

const navLinks = [
    { href: "/#linhas", label: "Linhas" },
    { href: "/#produtos", label: "Sabores" },
    { href: "/#kits", label: "Kits" },
    { href: "/onde-estamos", label: "Onde Estamos" },
    { href: "/parceiros", label: "Seja Parceiro", highlight: true },
    { href: "/#contacto", label: "Contacto" },
];

export const Header = () => {
    const { totals, setOpen } = useCart();
    const { user } = useCustomerAuth();
    const [scrolled, setScrolled] = useState(false);
    const [mobile, setMobile] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`sticky top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-white/85 backdrop-blur-xl border-b border-brand-green/10 shadow-sm"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
                <Link
                    to="/"
                    className="flex items-center gap-2"
                    data-testid="logo-link"
                    onClick={(e) => {
                        // If we're already on the home route, react-router won't
                        // re-navigate — force scroll to top and clear any hash so
                        // the user always sees the top of the page.
                        if (
                            typeof window !== "undefined" &&
                            window.location.pathname === "/"
                        ) {
                            e.preventDefault();
                            if (window.location.hash) {
                                window.history.replaceState(null, "", "/");
                            }
                            window.scrollTo({ top: 0, behavior: "smooth" });
                        }
                    }}
                >
                    <img
                        src="/brand/logo-main.png"
                        alt="Sabor Líquido"
                        className="h-12 md:h-14 w-auto"
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-7">
                    {navLinks.map((l) =>
                        l.href.startsWith("/#") ? (
                            <a
                                key={l.href}
                                href={l.href}
                                className={`text-sm font-medium transition-colors ${
                                    l.highlight
                                        ? "text-brand-green hover:text-brand-green-dark font-semibold"
                                        : "text-brand-ink-soft hover:text-brand-green"
                                }`}
                                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                            >
                                {l.label}
                            </a>
                        ) : (
                            <Link
                                key={l.href}
                                to={l.href}
                                className={`text-sm font-medium transition-colors ${
                                    l.highlight
                                        ? "inline-flex items-center gap-1.5 rounded-full bg-brand-green-soft text-brand-green hover:bg-brand-green hover:text-white px-4 py-1.5 font-bold"
                                        : "text-brand-ink-soft hover:text-brand-green"
                                }`}
                                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                            >
                                {l.highlight && <Handshake className="h-3.5 w-3.5" />}
                                {l.label}
                            </Link>
                        ),
                    )}
                </nav>

                <div className="flex items-center gap-2">
                    <Link
                        to={user ? "/conta" : "/conta/entrar"}
                        className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-white/80 hover:bg-white text-brand-ink px-4 py-2.5 text-sm font-semibold transition-colors border-2 border-brand-green/15 hover:border-brand-green"
                        data-testid="nav-account"
                        title={user ? `Conta de ${user.name}` : "Entrar / Registar"}
                    >
                        <User className="h-4 w-4" />
                        <span className="hidden md:inline">
                            {user ? user.name?.split(" ")[0] || "Conta" : "Entrar"}
                        </span>
                    </Link>

                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        className="relative inline-flex items-center gap-2 rounded-full bg-brand-ink text-brand-cream px-5 py-2.5 text-sm font-semibold hover:bg-[#2C312E] transition-colors shadow-pop"
                        data-testid="cart-trigger"
                    >
                        <ShoppingBag className="h-4 w-4" />
                        <span className="hidden sm:inline">Carrinho</span>
                        {totals.count > 0 && (
                            <span
                                className="absolute -top-1 -right-1 bg-brand-orange text-white text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center"
                                data-testid="cart-count-badge"
                            >
                                {totals.count}
                            </span>
                        )}
                    </button>

                    <button
                        type="button"
                        onClick={() => setMobile((s) => !s)}
                        className="lg:hidden p-2 rounded-full text-brand-ink"
                        data-testid="mobile-menu-toggle"
                        aria-label="Menu"
                    >
                        {mobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {mobile && (
                <div className="lg:hidden bg-white border-t border-brand-green/10">
                    <nav className="flex flex-col p-4 gap-1">
                        {navLinks.map((l) =>
                            l.href.startsWith("/#") ? (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    onClick={() => setMobile(false)}
                                    className={`px-4 py-3 rounded-xl text-sm font-medium ${
                                        l.highlight
                                            ? "bg-brand-green-soft text-brand-green font-bold"
                                            : "text-brand-ink hover:bg-brand-green-soft"
                                    }`}
                                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                                >
                                    {l.label}
                                </a>
                            ) : (
                                <Link
                                    key={l.href}
                                    to={l.href}
                                    onClick={() => setMobile(false)}
                                    className={`px-4 py-3 rounded-xl text-sm font-medium ${
                                        l.highlight
                                            ? "bg-brand-green-soft text-brand-green font-bold"
                                            : "text-brand-ink hover:bg-brand-green-soft"
                                    }`}
                                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                                >
                                    {l.label}
                                </Link>
                            ),
                        )}
                        <Link
                            to={user ? "/conta" : "/conta/entrar"}
                            onClick={() => setMobile(false)}
                            className="px-4 py-3 rounded-xl text-sm font-medium text-brand-ink hover:bg-brand-green-soft inline-flex items-center gap-2"
                            data-testid="mobile-nav-account"
                        >
                            <User className="h-4 w-4" />
                            {user ? `Conta (${user.name?.split(" ")[0]})` : "Entrar / Registar"}
                        </Link>
                    </nav>
                </div>
            )}
        </header>
    );
};
